import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AppLayout } from '../components/AppLayout';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import {
  Users, Briefcase, MapPin, Building2, ArrowLeft, FileDown,
  Clock, Loader2, CheckCircle2, XCircle, ExternalLink, Trophy, Medal, Calendar,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FAMILY_ICONS = {
  commerciaux: Users,
  business_manager: Briefcase,
  directeur_region: MapPin,
  direction: Building2,
};

const CATEGORIES = [
  { key: 'a_traiter', label: 'Étude à traiter (Direction)', short: 'À traiter', Icon: Clock,
    dot: 'bg-amber-500', text: 'text-amber-700',
    card: 'bg-amber-50 border-amber-200 hover:border-amber-400', active: 'ring-2 ring-amber-400 bg-amber-100' },
  { key: 'en_cours', label: 'Étude en cours', short: 'En cours', Icon: Loader2,
    dot: 'bg-blue-500', text: 'text-blue-700',
    card: 'bg-blue-50 border-blue-200 hover:border-blue-400', active: 'ring-2 ring-blue-400 bg-blue-100' },
  { key: 'signee', label: 'Étude signée', short: 'Signée', Icon: CheckCircle2,
    dot: 'bg-emerald-500', text: 'text-emerald-700',
    card: 'bg-emerald-50 border-emerald-200 hover:border-emerald-400', active: 'ring-2 ring-emerald-400 bg-emerald-100' },
  { key: 'perdue', label: 'Étude perdue / No Go', short: 'Perdue', Icon: XCircle,
    dot: 'bg-red-500', text: 'text-red-700',
    card: 'bg-red-50 border-red-200 hover:border-red-400', active: 'ring-2 ring-red-400 bg-red-100' },
];
const CAT_BY_KEY = Object.fromEntries(CATEGORIES.map((c) => [c.key, c]));

const MONTHS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const MEDALS = [
  { label: 'Or', ring: 'ring-yellow-400', bg: 'bg-yellow-50', chip: 'bg-yellow-400 text-yellow-900', icon: 'text-yellow-500' },
  { label: 'Argent', ring: 'ring-slate-300', bg: 'bg-slate-50', chip: 'bg-slate-300 text-slate-800', icon: 'text-slate-400' },
  { label: 'Bronze', ring: 'ring-orange-300', bg: 'bg-orange-50', chip: 'bg-orange-300 text-orange-900', icon: 'text-orange-400' },
];

const eur = (v) => (!v ? '0 €' : Math.round(v).toLocaleString('fr-FR') + ' €');

export default function DashboardCommercialPage() {
  const navigate = useNavigate();
  const { api, token } = useAuth();
  const [data, setData] = useState(null);
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selection, setSelection] = useState(null); // {family, category, personId?}
  const [exportCat, setExportCat] = useState('en_cours');
  const [exporting, setExporting] = useState(false);

  // Filtre période
  const [filterYear, setFilterYear] = useState(''); // '' = toutes années
  const [granularity, setGranularity] = useState('all'); // all | quarter | month
  const [filterQuarter, setFilterQuarter] = useState(1);
  const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1);

  const params = useMemo(() => {
    const p = {};
    if (filterYear) p.year = filterYear;
    if (granularity === 'quarter') p.quarter = filterQuarter;
    if (granularity === 'month') p.month = filterMonth;
    return p;
  }, [filterYear, granularity, filterQuarter, filterMonth]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api().get('/dashboard-commercial/summary', { params });
      setData(res.data);
      if (res.data?.years?.length) setYears(res.data.years);
      setSelection(null);
    } catch (e) {
      toast.error('Impossible de charger le dashboard commercial');
    } finally {
      setLoading(false);
    }
  }, [api, params]);

  useEffect(() => { load(); }, [load]);

  const selectedStudies = useMemo(() => {
    if (!selection || !data) return [];
    return data.studies
      .filter((s) => s.family === selection.family && s.category === selection.category
        && (!selection.personId || s.person_id === selection.personId))
      .sort((a, b) => (b.ca_previsionnel_ht || 0) - (a.ca_previsionnel_ht || 0));
  }, [selection, data]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const qs = new URLSearchParams({ category: exportCat, ...params }).toString();
      const res = await fetch(`${BACKEND_URL}/api/dashboard-commercial/export-pptx?${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('export failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `PIPE_COMMERCIAL_${exportCat.toUpperCase()}.pptx`;
      document.body.appendChild(a); a.click(); window.URL.revokeObjectURL(url); a.remove();
      toast.success('Export PowerPoint téléchargé');
    } catch (e) {
      toast.error("Erreur lors de l'export PowerPoint");
    } finally {
      setExporting(false);
    }
  };

  const isSelected = (family, category, personId) =>
    selection && selection.family === family && selection.category === category
    && (selection.personId || null) === (personId || null);

  const toggleSelect = (family, category, personId) => {
    if (isSelected(family, category, personId)) setSelection(null);
    else setSelection({ family, category, personId: personId || null });
  };

  const periodLabel = () => {
    if (!filterYear && granularity === 'all') return 'Toutes périodes';
    const y = filterYear || 'toutes années';
    if (granularity === 'quarter') return `T${filterQuarter} ${y}`;
    if (granularity === 'month') return `${MONTHS[filterMonth - 1]} ${y}`;
    return `Année ${y}`;
  };

  const Leaderboard = ({ title, Icon, entries, valueFmt, unit }) => (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5" data-testid={`dc-leaderboard-${unit}`}>
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-5 h-5 text-orange-500" />
        <h3 className="font-heading font-bold text-slate-900 dark:text-white">{title}</h3>
      </div>
      {(!entries || entries.length === 0) ? (
        <p className="text-sm text-slate-400 py-4 text-center">Aucune étude signée sur la période.</p>
      ) : (
        <div className="space-y-2">
          {entries.map((p, i) => {
            const m = MEDALS[i] || MEDALS[2];
            return (
              <div key={p.user_id + unit} className={`flex items-center gap-3 rounded-xl border p-3 ring-1 ${m.ring} ${m.bg}`} data-testid={`dc-podium-${unit}-${i + 1}`}>
                <span className={`w-8 h-8 rounded-full grid place-items-center font-bold text-sm ${m.chip}`}>{i + 1}</span>
                <Medal className={`w-5 h-5 ${m.icon}`} />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-900 dark:text-white text-sm truncate">{p.name}</p>
                  <p className="text-xs text-slate-500">{p.family_label}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-slate-900 dark:text-white tabular-nums">{valueFmt(p)}</p>
                  <p className="text-[11px] text-slate-400">{unit === 'ca' ? `${p.signed_count} contrat(s)` : eur(p.signed_ca)}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <AppLayout>
      <div className="space-y-6 animate-fade-in" data-testid="dashboard-commercial-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <button
              onClick={() => navigate('/simulations')}
              className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-orange-600 mb-2"
              data-testid="dc-back-btn"
            >
              <ArrowLeft className="w-4 h-4" /> Retour
            </button>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900 dark:text-white">
              Dashboard Commercial
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Vue d'ensemble des simulations créées, par profil et par catégorie · <span className="font-medium text-orange-600">{periodLabel()}</span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={exportCat}
              onChange={(e) => setExportCat(e.target.value)}
              className="h-10 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 text-sm text-slate-700 dark:text-slate-200"
              data-testid="dc-export-category-select"
            >
              {CATEGORIES.map((c) => (<option key={c.key} value={c.key}>{c.label}</option>))}
            </select>
            <Button onClick={handleExport} disabled={exporting}
              className="gap-2 bg-orange-500 hover:bg-orange-600 text-white" data-testid="dc-export-pptx-btn">
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
              Exporter PowerPoint
            </Button>
          </div>
        </div>

        {/* Filtre période */}
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/40 p-3" data-testid="dc-period-filter">
          <div className="flex items-center gap-1.5 text-sm font-medium text-slate-600 dark:text-slate-300">
            <Calendar className="w-4 h-4 text-orange-500" /> Période :
          </div>
          <select
            value={filterYear}
            onChange={(e) => setFilterYear(e.target.value ? parseInt(e.target.value, 10) : '')}
            className="h-9 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 text-sm"
            data-testid="dc-filter-year"
          >
            <option value="">Toutes les années</option>
            {years.map((y) => (<option key={y} value={y}>{y}</option>))}
          </select>

          <div className="inline-flex rounded-lg border border-slate-300 dark:border-slate-700 overflow-hidden">
            {[['all', 'Année'], ['quarter', 'Trimestre'], ['month', 'Mois']].map(([g, label]) => (
              <button
                key={g}
                onClick={() => setGranularity(g)}
                className={`px-3 h-9 text-sm font-medium transition-colors ${granularity === g ? 'bg-orange-500 text-white' : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 hover:bg-orange-50'}`}
                data-testid={`dc-granularity-${g}`}
              >
                {label}
              </button>
            ))}
          </div>

          {granularity === 'quarter' && (
            <select
              value={filterQuarter}
              onChange={(e) => setFilterQuarter(parseInt(e.target.value, 10))}
              className="h-9 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 text-sm"
              data-testid="dc-filter-quarter"
            >
              {[1, 2, 3, 4].map((q) => (<option key={q} value={q}>T{q}</option>))}
            </select>
          )}
          {granularity === 'month' && (
            <select
              value={filterMonth}
              onChange={(e) => setFilterMonth(parseInt(e.target.value, 10))}
              className="h-9 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 text-sm"
              data-testid="dc-filter-month"
            >
              {MONTHS.map((mn, i) => (<option key={i} value={i + 1}>{mn}</option>))}
            </select>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Chargement…
          </div>
        ) : !data || data.families.length === 0 ? (
          <div className="text-center py-20 text-slate-400" data-testid="dc-empty">
            Aucune simulation créée pour cette période.
          </div>
        ) : (
          <>
            {/* Palmarès commerciaux */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="dc-palmares">
              <Leaderboard title="Top 3 — CA signé total" Icon={Trophy}
                entries={data.leaderboards?.by_ca} unit="ca" valueFmt={(p) => eur(p.signed_ca)} />
              <Leaderboard title="Top 3 — Volume de contrats signés" Icon={Trophy}
                entries={data.leaderboards?.by_volume} unit="volume" valueFmt={(p) => `${p.signed_count}`} />
            </div>

            {/* Familles */}
            <div className="space-y-6">
              {data.families.map((fam) => {
                const FIcon = FAMILY_ICONS[fam.key] || Users;
                return (
                  <div key={fam.key}
                    className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden"
                    data-testid={`dc-family-${fam.key}`}>
                    <div className="flex items-center gap-3 px-5 py-4 bg-orange-50 dark:bg-orange-900/20 border-b border-orange-100 dark:border-orange-900/40">
                      <span className="w-10 h-10 rounded-xl grid place-items-center bg-orange-500/15 text-orange-600">
                        <FIcon className="w-5 h-5" />
                      </span>
                      <div className="flex-1">
                        <h2 className="font-heading font-bold text-lg text-slate-900 dark:text-white">{fam.label}</h2>
                        <p className="text-xs text-slate-500">{fam.totals.total} étude(s) · {fam.people.length} personne(s)</p>
                      </div>
                    </div>

                    <div className="p-5 space-y-5">
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                        {CATEGORIES.map((c) => (
                          <button
                            key={c.key}
                            onClick={() => toggleSelect(fam.key, c.key, null)}
                            className={`text-left rounded-xl border p-4 transition-all ${c.card} ${isSelected(fam.key, c.key, null) ? c.active : ''}`}
                            data-testid={`dc-cat-${fam.key}-${c.key}`}
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`w-2.5 h-2.5 rounded-full ${c.dot}`} />
                              <span className={`text-xs font-semibold ${c.text}`}>{c.label}</span>
                            </div>
                            <div className="text-3xl font-bold text-slate-900 dark:text-white tabular-nums">{fam.totals[c.key]}</div>
                          </button>
                        ))}
                      </div>

                      <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid={`dc-people-table-${fam.key}`}>
                          <thead>
                            <tr className="text-xs uppercase tracking-wide text-slate-400 border-b border-slate-200 dark:border-slate-700">
                              <th className="text-left py-2 px-2 font-semibold">Collaborateur</th>
                              {CATEGORIES.map((c) => (
                                <th key={c.key} className="text-center py-2 px-2 font-semibold">
                                  <span className={c.text}>{c.short}</span>
                                </th>
                              ))}
                              <th className="text-center py-2 px-2 font-semibold">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {fam.people.map((p) => (
                              <tr key={p.user_id} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40">
                                <td className="py-2 px-2 font-medium text-slate-800 dark:text-slate-200">{p.name}</td>
                                {CATEGORIES.map((c) => (
                                  <td key={c.key} className="text-center py-1.5 px-2">
                                    {p.totals[c.key] > 0 ? (
                                      <button
                                        onClick={() => toggleSelect(fam.key, c.key, p.user_id)}
                                        className={`inline-flex items-center justify-center min-w-[2rem] h-7 px-2 rounded-lg text-sm font-semibold transition-all ${c.card} ${c.text} ${isSelected(fam.key, c.key, p.user_id) ? c.active : ''}`}
                                        data-testid={`dc-person-cat-${p.user_id}-${c.key}`}
                                      >
                                        {p.totals[c.key]}
                                      </button>
                                    ) : (<span className="text-slate-300">–</span>)}
                                  </td>
                                ))}
                                <td className="text-center py-2 px-2 font-bold text-slate-900 dark:text-white">{p.totals.total}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {selection && selection.family === fam.key && (
                        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-800/40 p-4" data-testid="dc-study-list">
                          <div className="flex items-center gap-2 mb-3">
                            {(() => { const C = CAT_BY_KEY[selection.category]; const I = C.Icon; return <><I className={`w-4 h-4 ${C.text}`} /><h4 className={`font-semibold text-sm ${C.text}`}>{C.label}</h4></>; })()}
                            <span className="text-xs text-slate-400">
                              {selectedStudies.length} étude(s)
                              {selection.personId ? ` · ${fam.people.find((pp) => pp.user_id === selection.personId)?.name || ''}` : ''}
                            </span>
                          </div>
                          {selectedStudies.length === 0 ? (
                            <p className="text-sm text-slate-400 py-3 text-center">Aucune étude.</p>
                          ) : (
                            <div className="space-y-2">
                              {selectedStudies.map((s) => (
                                <button
                                  key={s.id}
                                  onClick={() => navigate(`/simulation/${s.id}`)}
                                  className="w-full flex items-center justify-between gap-3 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 px-4 py-3 hover:border-orange-400 hover:shadow-sm transition-all text-left group"
                                  data-testid={`dc-study-${s.id}`}
                                >
                                  <div className="min-w-0">
                                    <p className="font-semibold text-slate-900 dark:text-white text-sm truncate group-hover:text-orange-600">{s.name}</p>
                                    <p className="text-xs text-slate-400">{s.person_name} · CA prévi. HT {eur(s.ca_previsionnel_ht)}</p>
                                  </div>
                                  <ExternalLink className="w-4 h-4 text-slate-300 group-hover:text-orange-500 flex-shrink-0" />
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
}
